/**
 * @name pageDesigner
 *
 * @description
 * Contains all utilities about page designer mapping data that can be used in different components.
 */
import type {Page, LinkObject, SuggestionsColumn, ProductSuggestions, CLDImageAsset} from '_types'
import {createRedirectUrl} from '_utils'

// Flat shape produced by mapPageDesignerComponent for textItem/bannerNewsItem links (used by the PDP's packaging/shipment/return regions).
export interface PageContentLink {
    type?: 'text' | 'button'
    title?: string
    link?: string
}

// Flat shape produced by mapPageDesignerComponent for textItem/bannerNewsItem components.
export interface PageContentTextComponent {
    type: 'text' | 'button'
    id?: string
    name?: string
    text?: string
    links?: PageContentLink[]
}

// Flat shape produced by mapPageDesignerComponent for imageItem components.
export interface PageContentImageComponent {
    type: 'image'
    id?: string
    name?: string
    imageDesktop: CLDImageAsset
    imageTablet: CLDImageAsset
    imageMobile: CLDImageAsset
}

// Flat shape produced by mapPageDesignerComponent for pwaStoriesItem components (Stories page).
export interface PageContentStoriesItemComponent {
    type: 'storiesItem'
    id?: string
    name?: string
    title?: string
    description?: string
    link?: string
    fullWidth?: boolean
    imageZoomHover?: boolean
    image?: CLDImageAsset
    imageMobile?: CLDImageAsset
    mediaRatio?: string
    mediaRatioMobile?: string
}

// Flat shape produced by mapPageDesignerComponent for pwaStoriesComponent components (Stories page).
export interface PageContentStoriesLayoutComponent {
    type: 'storiesLayout'
    id?: string
    name?: string
    title?: string
    subtitle?: string
    sectionId?: string
    ctaLabel?: string
    visualLayout?: string
    items: (PageContentStoriesItemComponent | PageContentStoriesLayoutComponent)[]
}

// Structured Stories page content consumed by the Stories page and its sidebar navigation.
export interface StoriesPageContent {
    title?: string
    name?: string
    description?: string
    sections: PageContentStoriesLayoutComponent[]
    highlights: PageContentStoriesLayoutComponent[]
}

// Union of the component shapes a textBox/imageBox/PWA_packaging_pdp's `items` can hold (packaging/resi/spedizione regions).
export type PageContentComponent = PageContentTextComponent | PageContentImageComponent

/**
 * Maps the page designer data of search section.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} obj - An object containing ideas, women and men informations.
 * @returns {SuggestionsColumn} obj.ideasData - An object containing the title of the ideas column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.womenData - An object containing the title of the women column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.menData - An object containing the title of the men column and relative suggestion items.
 * @returns {ProductSuggestions} obj.productsData - An object containing the title of the products box and relative product items.
 */
export const mapSearchSectionPageDesignerData = (
    linkObject: LinkObject,
    pageComponents?: Page,
): {ideasData?: SuggestionsColumn; womenData?: SuggestionsColumn; menData?: SuggestionsColumn; productsData?: ProductSuggestions} => {
    const components = pageComponents?.regions?.[0]?.components

    let ideasData: SuggestionsColumn | undefined
    let womenData: SuggestionsColumn | undefined
    let menData: SuggestionsColumn | undefined
    let productsData: ProductSuggestions | undefined

    if (components) {
        const ideas = components[0]

        if (ideas?.data?.title) {
            ideasData = {
                title: {name: ideas?.data?.title, href: ideas?.data.link},
                items:
                    ideas?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const women = components[1]

        if (women?.data?.title) {
            womenData = {
                title: {name: women?.data?.title, href: women?.data.link},
                items:
                    women?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const men = components[2]

        if (men?.data?.title) {
            menData = {
                title: {name: men?.data?.title, href: men?.data.link},
                items:
                    men?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const products = components[3]

        if (products?.data?.title) {
            productsData = {
                title: products?.data?.title,
                productIds:
                    products?.regions?.[0]?.components?.filter((component) => component.data?.product)?.map((component) => component.data?.product) ?? [],
            }
        }
    }

    return {
        ideasData,
        womenData,
        menData,
        productsData,
    }
}

// Stable key fallback when component.id is missing.
export const stableKeyFromComponent = (component: any): string => {
    if (!component) return ''
    try {
        const typeId = component.typeId ?? ''
        const name = component.name ?? ''
        const data = component.data ?? {}
        const regions = component.regions ?? []
        return JSON.stringify({typeId, name, data, regions})
    } catch (_error) {
        return ''
    }
}

/**
 * @name slugifyStoriesSectionId
 *
 * @description
 * Builds a URL-safe slug from a Stories section title, used as a fallback when the
 * editor hasn't filled the `sectionId` attribute in Page Designer.
 *
 * @param {string} [title] - The section title.
 * @returns {string} The slug, or an empty string when no title is available.
 */
export const slugifyStoriesSectionId = (title?: string): string =>
    title
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') ?? ''

/**
 * @name mapPageDesignerComponent
 *
 * @description
 * Maps a single Page Designer component (from a region's `components` array) into the
 * flat shape consumed by the PDP (product-details) page content. Returns `undefined`
 * for component types that aren't handled yet.
 *
 * @param {any} subC - The raw Page Designer component.
 * @returns {any} The mapped component, or an array of mapped components for box-like types (textBox/imageBox).
 */
const mapPageDesignerComponent = (subC: any): any => {
     if (subC.typeId === 'commerce_assets.PWA_packaging_pdp') {
        return {
            type: 'PWA_packaging_pdp',
            id: subC.id,
            name: subC.name,
            // Sub-components can be either textItem or imageItem, so delegate to the same mapper used for those types.
            items:
                subC.regions
                    ?.flatMap((region) => region.components ?? [])
                    .map(mapPageDesignerComponent)
                    .filter(Boolean) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.PWA_sizeGuide_measurements_assets') {
        return {
            type: 'PWA_sizeGuide_measurements_assets',
            id: subC.id,
            name: subC.name,
            title: subC.data?.title,
            description: subC.data?.description,
            imageDesktop: subC.data?.cld_image_desktop,
            imageTablet: subC.data?.cld_image_tablet,
            imageMobile: subC.data?.cld_image_mobile,
            items:
                subC.regions
                    ?.flatMap((textBoxRegion) => textBoxRegion.components ?? [])
                    .map((textItem) => ({
                        type: 'PWA_sizeGuide_measurements_assets_item',
                        id: textItem.id,
                        name: textItem.name,
                        title: textItem.data?.title,
                        description: textItem.data?.description,
                    })) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.textBox') {
        return {
            type: 'textBox',
            id: subC.id,
            name: subC.name,
            // Children aren't necessarily textItem: delegate to the generic mapper to support any sub-component type.
            items:
                subC.regions
                    ?.flatMap((textBoxRegion) => textBoxRegion.components ?? [])
                    .map(mapPageDesignerComponent)
                    .filter(Boolean) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.textItem') {
        return {
            type: 'text',
            id: subC.id,
            name: subC.name,
            text: subC.data?.text,
            links: subC.regions
                ?.find((r) => r.id === 'links')
                ?.components?.map(
                    (link): PageContentLink => ({
                        title: link.data?.title,
                        link: link.data?.link,
                    }),
                ),
        } as PageContentTextComponent
    }
    if (subC.typeId === 'commerce_assets.bannerNewsItem') {
        const result: PageContentTextComponent = {
            type: 'text',
            id: subC.id,
            text: subC.data?.text,
            name: subC.name,
            links: subC.regions
                ?.find((r) => r.id === 'cta')
                ?.components?.map(
                    (link): PageContentLink => ({
                        type: link.data?.cta_type === 'button' ? 'button' : 'text',
                        title: link.data?.label_cta,
                        link: link.data?.link_cta,
                    }),
                ),
        }

        if (result.text === '{0}' && result.links?.[0]?.type === 'button') {
            result.type = 'button'
        }

        return result
    }
    if (subC.typeId === 'commerce_assets.imageBox') {
        return {
            type: 'imageBox',
            id: subC.id,
            name: subC.name,
            items:
                subC.regions
                    ?.flatMap((imageBoxRegion) => imageBoxRegion.components ?? [])
                    .map((imageItem) => ({
                        type: 'image',
                        id: imageItem.id,
                        name: imageItem.name,
                        imageDesktop: imageItem.data?.cld_image_desktop,
                        imageTablet: imageItem.data?.cld_image_tablet,
                        imageMobile: imageItem.data?.cld_image_mobile,
                    })) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.imageItem') {
        return {
            type: 'image',
            imageDesktop: subC.data?.cld_image_desktop,
            imageTablet: subC.data?.cld_image_tablet,
            imageMobile: subC.data?.cld_image_mobile,
            name: subC.name,
        }
    }
    if (subC.typeId === 'commerce_layouts.pwaStoriesMainLayout') {
        // Wrapper with no data of its own: flatten its `main` region directly into the parent's components list.
        return (
            subC.regions
                ?.find((region) => region.id === 'main')
                ?.components?.map(mapPageDesignerComponent)
                .filter(Boolean) ?? []
        )
    }
    if (subC.typeId === 'commerce_assets.pwaStoriesComponent') {
        return {
            type: 'storiesLayout',
            id: subC.id,
            name: subC.name,
            title: subC.data?.title,
            subtitle: subC.data?.subtitle,
            // Falls back to a slug of the title (or the component name, when no title is authored) so a
            // section is always addressable, even before an explicit sectionId is set.
            sectionId: subC.data?.sectionId || slugifyStoriesSectionId(subC.data?.title || subC.name),
            ctaLabel: subC.data?.ctaLabel,
            visualLayout: subC.data?.visualLayout,
            items:
                subC.regions
                    ?.find((region) => region.id === 'items')
                    ?.components?.flatMap(mapPageDesignerComponent)
                    .filter(Boolean) ?? [],
        } as PageContentStoriesLayoutComponent
    }
    if (subC.typeId === 'commerce_assets.pwaStoriesItem') {
        return {
            type: 'storiesItem',
            id: subC.id,
            name: subC.name,
            title: subC.data?.title,
            description: subC.data?.description,
            link: subC.data?.link,
            fullWidth: Boolean(subC.data?.fullWidth),
            imageZoomHover: subC.data?.image_hover_zoom !== false,
            image: subC.data?.image,
            imageMobile: subC.data?.image_mobile,
            mediaRatio: subC.data?.media_ratio,
            mediaRatioMobile: subC.data?.media_ratio_mobile,
        } as PageContentStoriesItemComponent
    }

    return undefined
}

/**
 * @name mapProductDetailsPageContent
 *
 * @description
 * Maps the PDP's Page Designer regions into a `{[regionId]: components[]}` object,
 * flattening each region's components via `mapPageDesignerComponent`.
 *
 * @param {Page} [pageDesignerData] - The Page Designer page data for the PDP.
 * @returns {Record<string, any[]>} The mapped page content, keyed by region id.
 */
export const mapProductDetailsPageContent = (pageDesignerData?: Page): Record<string, any[]> =>
    Object.fromEntries(pageDesignerData?.regions?.map((region) => [region.id, region.components?.flatMap(mapPageDesignerComponent) ?? []]) ?? [])

/**
 * @name STORIES_VISUAL_LAYOUTS
 *
 * @description
 * The fixed visual layouts an editor can pick on a `pwaStoriesComponent`, mapped to how many
 * stories are rendered in the collapsed view. Layout 3 drops to 2 items on mobile and
 * layout 4 renders its full-width banner plus 2 (mobile) or 3 (desktop) stories below.
 */
export const STORIES_VISUAL_LAYOUTS = {
    BANNER_FULL: '1 - Banner full',
    TWO_STORIES: '2 - Due storie',
    THREE_STORIES: '3 - Tre storie',
    BANNER_FULL_WITH_THREE: '4 - Banner full e tre storie sotto',
} as const

/**
 * @name getStoriesVisibleItemsCount
 *
 * @description
 * Returns how many stories of a section are visible in the collapsed view, given its
 * visual layout and the current viewport.
 *
 * @param {string} [visualLayout] - The `visualLayout` attribute of the section.
 * @param {boolean} [isMobile] - Whether the mobile breakpoint is active.
 * @returns {number} The number of stories to render.
 */
export const getStoriesVisibleItemsCount = (visualLayout?: string, isMobile = false): number => {
    switch (visualLayout) {
        case STORIES_VISUAL_LAYOUTS.TWO_STORIES:
            return 2
        case STORIES_VISUAL_LAYOUTS.THREE_STORIES:
            return isMobile ? 2 : 3
        case STORIES_VISUAL_LAYOUTS.BANNER_FULL_WITH_THREE:
            return isMobile ? 3 : 4
        case STORIES_VISUAL_LAYOUTS.BANNER_FULL:
            return 1
        default:
            return isMobile ? 2 : 3
    }
}

/**
 * @name isStoriesLayoutComponent
 *
 * @description
 * Type guard distinguishing a nested section from a story card inside a `storiesLayout`'s
 * generic `items` list.
 *
 * @param {PageContentStoriesItemComponent | PageContentStoriesLayoutComponent} item - The mapped item.
 * @returns {boolean} Whether the item is a nested section.
 */
export const isStoriesLayoutComponent = (
    item: PageContentStoriesItemComponent | PageContentStoriesLayoutComponent,
): item is PageContentStoriesLayoutComponent => item.type === 'storiesLayout'

/**
 * @name isStoriesItemComponent
 *
 * @description
 * Type guard distinguishing a story card from a nested section inside a `storiesLayout`'s
 * generic `items` list.
 *
 * @param {PageContentStoriesItemComponent | PageContentStoriesLayoutComponent} item - The mapped item.
 * @returns {boolean} Whether the item is a story card.
 */
export const isStoriesItemComponent = (
    item: PageContentStoriesItemComponent | PageContentStoriesLayoutComponent,
): item is PageContentStoriesItemComponent => item.type === 'storiesItem'

/**
 * @name mapStoriesPageContent
 *
 * @description
 * Maps the Stories Page Designer page into the structured content consumed by the Stories page:
 * the page title (the Page Designer page `name`), the optional intro paragraph held by the
 * `pwaStoriesMainLayout` wrapper, the list of sections, and the sections from its dedicated
 * `highlights` region.
 *
 * @param {Page} [pageDesignerData] - The Stories Page Designer page data.
 * @returns {StoriesPageContent} The structured Stories page content.
 */
export const mapStoriesPageContent = (pageDesignerData?: Page): StoriesPageContent => {
    const pageComponents = pageDesignerData?.regions?.flatMap((region) => region.components ?? []) ?? []
    const mainLayout = pageComponents.find((component: any) => component.typeId === 'commerce_layouts.pwaStoriesMainLayout') as any
    const sections = pageComponents.flatMap(mapPageDesignerComponent).filter(Boolean) as PageContentStoriesLayoutComponent[]
    const highlights =
        (mainLayout?.regions
            ?.find((region: any) => region.id === 'highlights')
            ?.components?.flatMap(mapPageDesignerComponent)
            .filter(isStoriesLayoutComponent) as PageContentStoriesLayoutComponent[]) ?? []

    return {
        title: mainLayout?.name,
        description: mainLayout?.data?.description,
        sections,
        highlights,
    }
}